import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { Metadata, RunView, CompositeKey } from '@memberjunction/core';
import { Subject } from 'rxjs';
import { MJCredentialEntity, MJCredentialTypeEntity } from '@memberjunction/core-entities';
import { BCSaaSOrganizationCredentialEntity } from '@bluecypress/bcsaas-entities';
import { mjBizAppsCommonOrganizationEntity } from '@mj-biz-apps/common-entities';
import { IzzyMetadataEngine } from 'mj_generatedentities';
import { IAdminPanel, AdminPanelStatus } from '../admin-panel.interface';
import { CreateOrgCredential, CredentialNameSuffix, DeleteOrgCredential, DisplayCredentialName } from '../../utils/org-credentials';

interface CredentialDisplay {
  credential: MJCredentialEntity;
  credentialType: MJCredentialTypeEntity | null;
  isEditing: boolean;
  editForm: CredentialEditForm;
  editSchemaFields: SchemaField[];
}

interface CredentialEditForm {
  name: string;
  description: string;
  credentialTypeId: string;
  status: 'Active' | 'Inactive';
  expiresAt: string;
  credentialData: Record<string, string>;
}

interface CredentialsState {
  selectedOrganizationId?: string;
}

interface SchemaField {
  name: string;
  label: string;
  type: string;
  required: boolean;
  help?: string;
}

/**
 * Credentials Management Component
 *
 * Provides a user-friendly interface for managing organization credentials:
 * - View all credentials for an organization
 * - Create new credentials with type-specific forms
 * - Edit credential metadata (name, status, expiration)
 * - Delete credentials
 *
 * This component does NOT display or allow editing of actual credential values
 * (like API keys) for security - those are only set during creation.
 */
@Component({
  standalone: false,
  selector: 'izzy-credentials',
  templateUrl: './credentials.component.html',
  styleUrls: ['./credentials.component.css']
})
export class CredentialsComponent implements OnInit, OnDestroy, OnChanges, IAdminPanel {

  @Input() initialState: CredentialsState | null = null;
  @Input() organizationId: string | undefined;
  @Output() stateChange = new EventEmitter<CredentialsState>();
  @Output() statusChange = new EventEmitter<AdminPanelStatus>();
  @Output() openRecord = new EventEmitter<{ EntityName: string; RecordPKey: CompositeKey }>();

  constructor() {}

  // Data
  protected organizations: mjBizAppsCommonOrganizationEntity[] = [];
  protected credentials: MJCredentialEntity[] = [];
  protected credentialTypes: MJCredentialTypeEntity[] = [];
  protected credentialDisplays: CredentialDisplay[] = [];

  // Selection State
  protected selectedOrganization: mjBizAppsCommonOrganizationEntity | null = null;

  // UI State
  protected isLoading: boolean = true;
  protected isSaving: boolean = false;
  protected errorMessage: string = '';
  protected successMessage: string = '';
  protected searchQuery: string = '';

  // Create Mode
  protected isCreating: boolean = false;
  protected createForm: CredentialEditForm = this.getEmptyForm();
  protected schemaFields: SchemaField[] = [];

  // Confirm Dialog
  protected confirmDialogVisible: boolean = false;
  protected confirmDialogTitle: string = '';
  protected confirmDialogMessage: string = '';
  protected confirmDialogAction: (() => Promise<void>) | null = null;

  private destroy$ = new Subject<void>();

  async ngOnInit(): Promise<void> {
    await this.loadData();

    // Apply initial state — prefer organizationId input, then initialState, then first org
    const targetOrgId = this.organizationId ?? this.initialState?.selectedOrganizationId;
    const targetOrg = targetOrgId
      ? this.organizations.find(o => o.ID === targetOrgId)
      : this.organizations[0];
    if (targetOrg) {
      this.selectOrganization(targetOrg);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // React to organizationId input changes
    if (changes['organizationId'] && !changes['organizationId'].firstChange) {
      const newOrgId = changes['organizationId'].currentValue;
      if (newOrgId && this.organizations.length > 0) {
        const org = this.organizations.find(o => o.ID === newOrgId);
        if (org && org.ID !== this.selectedOrganization?.ID) {
          this.selectOrganization(org);
        }
      }
    }
  }

  private getEmptyForm(): CredentialEditForm {
    return {
      name: '',
      description: '',
      credentialTypeId: '',
      status: 'Active',
      expiresAt: '',
      credentialData: {}
    };
  }

  protected async loadData(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = '';

    try {
      await IzzyMetadataEngine.Instance.Config(false);
      this.organizations = IzzyMetadataEngine.Instance.GetCurrentUserOrganizations();

      // Load credential types
      const rv = new RunView();
      const typesResult = await rv.RunView<MJCredentialTypeEntity>({
        EntityName: 'MJ: Credential Types',
        OrderBy: 'Name ASC'
      });
      this.credentialTypes = typesResult.Success ? typesResult.Results : [];

    } catch (error) {
      console.error('Error loading credentials data:', error);
      this.errorMessage = 'Failed to load data. Please try again.';
    } finally {
      this.isLoading = false;
    }
  }

  private async loadCredentials(): Promise<void> {
    if (!this.selectedOrganization) {
      this.credentials = [];
      this.credentialDisplays = [];
      return;
    }

    try {
      const rv = new RunView();

      // Load org-credential bridge records to find which credentials belong to this org
      const bridgeResult = await rv.RunView<BCSaaSOrganizationCredentialEntity>({
        EntityName: 'BC: Organization Credentials',
        ExtraFilter: `OrganizationID = '${this.selectedOrganization.ID}' AND Status = 'Active'`,
        ResultType: 'entity_object'
      });

      const bridgeRecords = bridgeResult.Success ? bridgeResult.Results : [];

      if (bridgeRecords.length === 0) {
        this.credentials = [];
        this.credentialDisplays = [];
        this.emitPanelStatus();
        return;
      }

      // Load the actual credential entities by IDs from the bridge
      const credentialIds = bridgeRecords.map(b => `'${b.CredentialID}'`).join(', ');
      const result = await rv.RunView<MJCredentialEntity>({
        EntityName: 'MJ: Credentials',
        ExtraFilter: `ID IN (${credentialIds})`,
        OrderBy: 'Name ASC',
        ResultType: 'entity_object'
      });

      this.credentials = result.Success ? result.Results : [];
      this.credentialDisplays = this.credentials.map(c => ({
        credential: c,
        credentialType: this.credentialTypes.find(t => t.ID === c.CredentialTypeID) || null,
        isEditing: false,
        editForm: this.credentialToForm(c),
        editSchemaFields: []
      }));

      // Emit status after credentials are loaded
      this.emitPanelStatus();
    } catch (error) {
      console.error('Error loading credentials:', error);
      this.errorMessage = 'Failed to load credentials.';
    }
  }

  /** Credential name without BCSaaS's ` [organizationID]` suffix (template helper). */
  protected readonly displayName = DisplayCredentialName;

  private credentialToForm(credential: MJCredentialEntity): CredentialEditForm {
    return {
      name: DisplayCredentialName(credential.Name),
      description: credential.Description || '',
      credentialTypeId: credential.CredentialTypeID,
      status: credential.IsActive ? 'Active' : 'Inactive',
      expiresAt: credential.ExpiresAt ? this.formatDateForInput(credential.ExpiresAt) : '',
      credentialData: {} // Never load actual credential values for security
    };
  }

  private formatDateForInput(date: Date): string {
    if (!date) return '';
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  }

  get filteredCredentials(): CredentialDisplay[] {
    return this.credentialDisplays.filter(cd => {
      const matchesSearch = !this.searchQuery.trim() ||
        cd.credential.Name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        (cd.credential.Description && cd.credential.Description.toLowerCase().includes(this.searchQuery.toLowerCase())) ||
        (cd.credentialType?.Name && cd.credentialType.Name.toLowerCase().includes(this.searchQuery.toLowerCase()));
      return matchesSearch;
    });
  }

  /**
   * Returns true if we have existing credentials (to show header Add button)
   * When there are no credentials, we show the empty state which has its own Add button
   */
  get hasCredentials(): boolean {
    return this.credentialDisplays.length > 0;
  }

  // Organization Selection

  selectOrganization(org: mjBizAppsCommonOrganizationEntity): void {
    this.selectedOrganization = org;
    this.cancelCreate();
    this.loadCredentials();
    this.emitStateChange();
  }

  onOrganizationChange(orgId: string): void {
    const org = this.organizations.find(o => o.ID === orgId);
    if (org) {
      this.selectOrganization(org);
    }
  }

  // Create Mode

  startCreate(): void {
    this.isCreating = true;
    this.createForm = this.getEmptyForm();
    this.cancelAllEdits();
  }

  cancelCreate(): void {
    this.isCreating = false;
    this.createForm = this.getEmptyForm();
    this.schemaFields = [];
  }

  onTypeChange(typeId: string): void {
    this.createForm.credentialTypeId = typeId;
    this.createForm.credentialData = {};
    this.updateSchemaFields();
  }

  /**
   * True when the selected credential type is an AI provider credential
   * (Category = 'AI' on the CredentialType). Drives the tier-guidance panel.
   */
  protected isAICredentialType(typeId: string | null | undefined): boolean {
    if (!typeId) return false;
    const type = this.credentialTypes.find(t => t.ID === typeId);
    return type?.Category === 'AI';
  }

  private updateSchemaFields(): void {
    this.schemaFields = this.getSchemaFieldsForType(this.createForm.credentialTypeId);
  }

  private getSchemaFieldsForType(typeId: string): SchemaField[] {
    const type = this.credentialTypes.find(t => t.ID === typeId);
    if (!type?.FieldSchema) {
      return [];
    }

    try {
      const schema = typeof type.FieldSchema === 'string'
        ? JSON.parse(type.FieldSchema)
        : type.FieldSchema;

      if (!schema?.properties) {
        return [];
      }

      const required = schema.required || [];
      return Object.entries(schema.properties).map(([name, prop]: [string, any]) => ({
        name,
        label: prop.title || name,
        type: prop.type === 'string' && (name.toLowerCase().includes('secret') || name.toLowerCase().includes('password') || name.toLowerCase().includes('token') || name.toLowerCase().includes('key')) ? 'password' : 'text',
        required: required.includes(name),
        help: prop.description
      }));
    } catch (e) {
      console.error('Error parsing credential schema:', e);
      return [];
    }
  }

  async saveCreate(): Promise<void> {
    if (!this.selectedOrganization || !this.createForm.name.trim() || !this.createForm.credentialTypeId) {
      this.errorMessage = 'Name and type are required.';
      return;
    }

    // Validate required fields from schema
    for (const field of this.schemaFields) {
      if (field.required && !this.createForm.credentialData[field.name]?.trim()) {
        this.errorMessage = `${field.label} is required.`;
        return;
      }
    }

    const credentialType = this.credentialTypes.find(t => t.ID === this.createForm.credentialTypeId);
    if (!credentialType) {
      this.errorMessage = 'Unknown credential type. Please refresh and try again.';
      return;
    }

    this.isSaving = true;
    this.errorMessage = '';

    try {
      // Through BCSaaS's credential engine: MJ credential + organization link + (for an AI vendor key)
      // the vendor's credential-use junction, in one call. Values go up as plain JSON and are
      // encrypted server-side as before. The engine always creates the credential active.
      const values: Record<string, string> = {};
      for (const [key, value] of Object.entries(this.createForm.credentialData)) {
        values[key] = typeof value === 'string' ? value.trim() : value;
      }
      await CreateOrgCredential({
        organizationID: this.selectedOrganization.ID,
        credentialTypeName: credentialType.Name,
        name: this.createForm.name.trim(),
        values,
        description: this.createForm.description.trim() || null,
        expiresAt: this.createForm.expiresAt ? new Date(this.createForm.expiresAt) : null,
      });

      await this.loadCredentials();
      this.showSuccess('Credential created');
      this.cancelCreate();
    } catch (error) {
      console.error('Error creating credential:', error);
      this.errorMessage = `Failed to create credential: ${error instanceof Error ? error.message : String(error)}`;
    } finally {
      this.isSaving = false;
    }
  }

  // Edit Mode (metadata only, not credential values)

  startEdit(display: CredentialDisplay): void {
    this.cancelAllEdits();
    this.cancelCreate();
    display.isEditing = true;
    display.editForm = this.credentialToForm(display.credential);
    display.editSchemaFields = this.getSchemaFieldsForType(display.credential.CredentialTypeID);
  }

  cancelEdit(display: CredentialDisplay): void {
    display.isEditing = false;
    display.editForm = this.credentialToForm(display.credential);
  }

  cancelAllEdits(): void {
    for (const display of this.credentialDisplays) {
      display.isEditing = false;
      display.editForm = this.credentialToForm(display.credential);
    }
  }

  async saveEdit(display: CredentialDisplay): Promise<void> {
    this.isSaving = true;
    this.errorMessage = '';

    try {
      const credential = display.credential;

      // Keep BCSaaS's ` [organizationID]` suffix on credentials that carry it; the form shows the name without it.
      credential.Name = display.editForm.name.trim() + CredentialNameSuffix(credential.Name);
      credential.Description = display.editForm.description.trim() || null;
      credential.IsActive = display.editForm.status === 'Active';

      if (display.editForm.expiresAt) {
        credential.ExpiresAt = new Date(display.editForm.expiresAt);
      } else {
        credential.ExpiresAt = null;
      }

      // Update credential values if any were provided
      // Only include fields that have values (user may leave them blank to keep existing)
      const newCredentialData: Record<string, string> = {};
      let hasNewCredentialData = false;

      for (const field of display.editSchemaFields) {
        const value = display.editForm.credentialData[field.name];
        if (value && value.trim()) {
          newCredentialData[field.name] = value.trim();
          hasNewCredentialData = true;
        }
      }

      if (hasNewCredentialData) {
        // Send new credential data as JSON - server will encrypt
        credential.Values = JSON.stringify(newCredentialData);
      }

      const saveResult = await credential.Save();
      if (!saveResult) {
        throw new Error('Failed to save credential');
      }

      await this.loadCredentials();
      this.showSuccess('Credential updated');
    } catch (error) {
      console.error('Error updating credential:', error);
      this.errorMessage = 'Failed to update credential. Please try again.';
    } finally {
      this.isSaving = false;
    }
  }

  // Delete

  confirmDelete(display: CredentialDisplay): void {
    this.showConfirmDialog(
      'Delete Credential',
      `Are you sure you want to delete "${DisplayCredentialName(display.credential.Name)}"? Any actions using this credential will lose access.`,
      async () => {
        await this.deleteCredential(display);
      }
    );
  }

  private async deleteCredential(display: CredentialDisplay): Promise<void> {
    this.isSaving = true;
    this.errorMessage = '';

    try {
      if (!this.selectedOrganization) {
        throw new Error('No organization selected');
      }
      // Through BCSaaS's credential engine: junction rows + organization link, then the credential
      // itself when no other organization uses it, so nothing is left orphaned.
      await DeleteOrgCredential(this.selectedOrganization.ID, display.credential.ID);

      await this.loadCredentials();
      this.showSuccess('Credential deleted');
    } catch (error) {
      console.error('Error deleting credential:', error);
      this.errorMessage = `Failed to delete credential: ${error instanceof Error ? error.message : String(error)}`;
    } finally {
      this.isSaving = false;
    }
  }

  // Helpers

  getCredentialStatusLabel(credential: MJCredentialEntity): string {
    if (this.isExpired(credential)) return 'Expired';
    return credential.IsActive ? 'Active' : 'Inactive';
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Active': return 'status-active';
      case 'Inactive': return 'status-inactive';
      case 'Expired': return 'status-expired';
      default: return '';
    }
  }

  getTypeIcon(typeName: string): string {
    const lowerName = (typeName || '').toLowerCase();
    if (lowerName.includes('graph') || lowerName.includes('microsoft') || lowerName.includes('azure')) return 'fa-microsoft';
    if (lowerName.includes('gmail') || lowerName.includes('google')) return 'fa-google';
    if (lowerName.includes('hubspot')) return 'fa-hubspot';
    if (lowerName.includes('salesforce')) return 'fa-salesforce';
    if (lowerName.includes('twilio') || lowerName.includes('sms')) return 'fa-comment-sms';
    if (lowerName.includes('oauth')) return 'fa-key';
    if (lowerName.includes('api')) return 'fa-code';
    return 'fa-lock';
  }

  isExpiringSoon(credential: MJCredentialEntity): boolean {
    if (!credential.ExpiresAt) return false;
    const expiresAt = new Date(credential.ExpiresAt);
    const now = new Date();
    const daysUntilExpiry = (expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
    return daysUntilExpiry > 0 && daysUntilExpiry <= 30;
  }

  isExpired(credential: MJCredentialEntity): boolean {
    if (!credential.ExpiresAt) return false;
    return new Date(credential.ExpiresAt) < new Date();
  }

  openCredentialRecord(display: CredentialDisplay): void {
    this.openRecord.emit({
      EntityName: 'MJ: Credentials',
      RecordPKey: CompositeKey.FromID(display.credential.ID)
    });
  }

  // Confirm Dialog

  private showConfirmDialog(title: string, message: string, action: () => Promise<void>): void {
    this.confirmDialogTitle = title;
    this.confirmDialogMessage = message;
    this.confirmDialogAction = action;
    this.confirmDialogVisible = true;
  }

  protected async confirmDialogYes(): Promise<void> {
    if (this.confirmDialogAction) {
      await this.confirmDialogAction();
    }
    this.closeConfirmDialog();
  }

  protected closeConfirmDialog(): void {
    this.confirmDialogVisible = false;
    this.confirmDialogAction = null;
  }

  // State Management

  private emitStateChange(): void {
    this.stateChange.emit({
      selectedOrganizationId: this.selectedOrganization?.ID
    });
  }

  private showSuccess(message: string): void {
    this.successMessage = message;
    setTimeout(() => { this.successMessage = ''; }, 3000);
  }

  // ========== Admin Panel Status ==========

  private emitPanelStatus(): void {
    const status = this.calculatePanelStatus();
    this.statusChange.emit(status);
  }

  private calculatePanelStatus(): AdminPanelStatus {
    if (!this.selectedOrganization) {
      return { status: 'none', tooltip: '' };
    }

    const credentialCount = this.credentialDisplays.length;
    const expiredCount = this.credentialDisplays.filter(cd => this.isExpired(cd.credential)).length;
    const expiringSoonCount = this.credentialDisplays.filter(cd => this.isExpiringSoon(cd.credential)).length;

    if (credentialCount === 0) {
      return {
        status: 'warning',
        tooltip: 'No credentials configured'
      };
    }

    if (expiredCount > 0) {
      return {
        status: 'error',
        tooltip: `${expiredCount} credential(s) expired`
      };
    }

    if (expiringSoonCount > 0) {
      return {
        status: 'warning',
        tooltip: `${expiringSoonCount} credential(s) expiring soon`
      };
    }

    return {
      status: 'good',
      tooltip: `${credentialCount} active credential(s)`
    };
  }
}

export function LoadCredentialsComponent(): void {}
